<template>
  <div class="product-form">
    <form
      class="product-form__form"
      @submit.prevent="handleAddToCart"
    >
      <p
        v-if="product.customId"
        class="product-form__custom-id text-body-2xs"
        :class="{ 'product-form__custom-id--absolute': product && product.colours }"
        v-text="product.customId"
      />

      <colour-swatches
        v-if="product && product.colours"
        :class="isQuickview ? 'quick-view__swatches' : 'product-form__colour-swatches'"
        :colours="product.colours"
        :handle="product.handle"
        :is-quickview="isQuickview"
        labels
        large
        :links="!isQuickview"
        :state="state"
        @update-handle="handleUpdateHandle"
      />

      <product-variants
        ref="variantSection"
        class="product-form__variants"
        :low-stock-threshold="lowStockThreshold"
        :matched-brand="matchedBrand"
        :product="product"
        :show-size-guide="isQuickview ? false : true"
        :variant="variant"
        @update-variant="handleVariantUpdate"
      />

      <div class="product-form__footer">
        <btn
          ref="addToCartButton"
          class="product-form__add-to-cart"
          :class="{
            [$class('disabled')]: addToCartDisabled,
            [$class('updating')]: state === 'adding' || state === 'added',
            [$class('fixed')]: isFixedAddToCart,
          }"
          block-class="product-form"
          :label="addToCartText"
          modifiers="block"
          :show-icon="false"
          :type="variant.available ? 'submit' : 'button'"
          @click="handleSubmit"
        />

        <p
          v-if="error"
          class="product-form__error text-body-s text-center"
          v-text="error"
        />
      </div>
      <div ref="addToCartObserver" />
    </form>

    <div
      ref="dynamicCheckoutButtons"
      class="product-form__dynamic-checkout-buttons"
      :class="{ [$class('disabled')]: !variant }"
    />

    <div
      v-if="isMobile && hasCompleteTheLook"
      class="product-form__complete-look"
    >
      <button
        v-if="hasCompleteTheLook"
        class="product-form__complete-look-button-mobile"
        type="button"
        @click.prevent="scrollToCompleteTheLook"
      >
        <span
          class="product-form__complete-look-text"
          v-text="$string('general.complete_the_look.title')"
        />
        <icon-chevron-down class="product-form__complete-look-icon" />
      </button>
    </div>

    <btn
      v-if="!variant"
      class="product-form__sticky-size-selector"
      :class="{ [$class('fixed')]: showStickySizeSelector }"
      block-class="product-form"
      :label="$string('product.cta.select_size')"
      modifiers="block"
      :show-icon="false"
      type="button"
      @click="scrollToVariants"
    />
  </div>
</template>

<script>

/**
 * Vue: Product form (product-form)
 * -----------------------------------------------------------------------------
 * Product form to handle variant selection and adding to cart.
 *
 * @param {Boolean} [isQuickview] - If being used by a QuickView component.
 * @param {Number} lowStockThreshold - Low stock warning threshold.
 * @param {Object|Boolean} matchedBrand - Matched brand for size guide.
 * @param {Object} product - Liquid local product object.
 * @param {Object|Boolean} variant - Canvas formatted variant object.
 * @emits updateHandle - Emits handle when new colour swatch is selected.
 */
import { mapActions, mapGetters, mapState } from 'vuex'

import Btn from '~global/btn/btn'
import ColourSwatches from '~async/colour-swatches/colour-swatches'
import ProductVariants from '~async/product-form/product-variants'

import { moveAsyncElement, scrollToElement } from '~/helpers/general'
import IconChevronDown from '~icons/direction/chevron/down.svg'

export default {
  name: 'ProductForm',

  components: {
    Btn,
    ColourSwatches,
    ProductVariants,
    IconChevronDown,
  },

  props: {
    isQuickview: {
      type: Boolean,
      default: false,
    },
    lowStockThreshold: {
      type: Number,
      default: 5,
    },
    matchedBrand: {
      type: [Boolean, Object],
      default: () => ({}),
    },
    product: {
      type: [Boolean, Object],
      default: () => ({
        hasOnlyDefaultVariant: false,
      }),
      required: true,
    },
    variant: {
      type: [Boolean, Object],
      default: false,
      required: true,
    },
  },

  emits: {
    'update-handle': (payload) => typeof payload === 'string',
    'update-variant': (payload) => typeof payload === 'object',
  },

  data() {
    return {
      enableBis: false,
      error: '',
      keyBis: 0,
      lineItemProperties: {},
      quantity: 1,
      selectors: {
        id: '[js-dynamic-checkout-buttons="id"]',
      },
      state: 'ready',
      timeout: {},
      isFixedAddToCart: false,
      intersectionObserver: null,
    }
  },

  computed: {

    /**
     * Map Vuex getters.
     */
    ...mapGetters({
      getActiveOverlay: 'overlays/getActiveOverlay',
      getComponentExists: 'components/getExists',
    }),

    /**
     * Map Vuex state.
     */
    ...mapState({
      isMobile: (state) => state.index.screen.mobile,
    }),

    /**
     * Check if the product has a complete the look section
     * @returns {Boolean}
     */
    hasCompleteTheLook() {
      return Boolean(this.product?.complete_the_look)
    },

    /**
     * Compute if add to cart button is disabled.
     * @returns {Boolean}
     */
    addToCartDisabled() {
      return (
        this.state === 'loading' ||
        !this.variant ||
        (!this.variant.available && this.hideBis) ||
        this.product.type.toLowerCase() === 'gift wrapping' ||
        this.product.tags.includes('hide_plp')
      )
    },

    /**
     * Compute add to cart text.
     * @returns {String}
     */
    addToCartText() {
      if (!this.variant && this.product.available) {
        return this.$string('product.cta.select_size')
      }

      if (!this.variant.available) {
        return this.hideBis
          ? this.$string('product.cta.sold_out')
          : this.$string('product.cta.back_in_stock')
      }

      switch (this.state) {
        case 'adding':
          return this.$string('product.cta.adding_to_cart')
        case 'added':
          return this.$string('product.cta.added_to_cart')
        default:
          return this.$string('product.cta.add_to_cart')
      }
    },

    /**
     * Compute whether back in stock hidden tag exists.
     * @returns {Boolean}
     */
    hideBis() {
      return this.product?.tags?.includes('hide_bis')
    },

    /**
     * Determines if the "Select Size" button should be shown.
     * Shown when no variant is selected and the device is mobile.
     * @returns {Boolean}
     */
    showSelectSizeButton() {
      return !this.variant && this.isMobile
    },

    /**
     * Determines if the sticky size selector should be shown.
     * Shown when no variant is selected, on mobile, and add to cart is fixed.
     * @returns {Boolean}
     */
    showStickySizeSelector() {
      return !this.variant && this.isMobile && this.isFixedAddToCart
    },
  },

  watch: {

    /**
     * Watch variant update to clear error message.
     */
    variant() {
      this.error = ''

      /**
       * Update dynamic checkout buttons form variant ID.
       */
      const id = document.querySelector(this.selectors.id)

      if (!id) {
        return
      }

      id.value = this.variant.id
    },

    /**
     * Reset fixed add to cart button state when switching to desktop
     */
    isMobile(newValue) {
      if (!newValue && this.isFixedAddToCart) {
        this.isFixedAddToCart = false
      }
    },
  },

  mounted() {
    moveAsyncElement(
      '[js-dynamic-checkout-buttons="container"]',
      this.$refs.dynamicCheckoutButtons,
    )
    this.initShopifyPaymentButton()
    this.handleIntersectionObserver()
  },

  beforeUnmount() {
    if (this.intersectionObserver) {
      this.intersectionObserver.disconnect()
    }
  },

  methods: {

    /**
     * Map Vuex actions.
     */
    ...mapActions({
      addItem: 'cart/addItem',
      openOverlay: 'overlays/open',
      queueOverlay: 'overlays/queue',
      setBackInStockProductData: 'back-in-stock/setProductData',
    }),

    /**
     * Handle handle update.
     * - For quickview component.
     * @param {String} handle - Selected handle.
     */
    handleUpdateHandle(handle) {
      if (!this.isQuickview) {
        return
      }

      this.$emit('update-handle', handle)
    },

    /**
     * Handle variant update.
     * @param {Object} variant - Selected variant.
     */
    handleVariantUpdate(variant) {
      this.$emit('update-variant', variant)
    },

    /**
     * Handle form submit.
     */
    handleSubmit(event) {
      if (this.variant.available || this.hideBis) {
        return
      }

      event.preventDefault()
      this.enableBis = true
      this.keyBis += 1

      const queueQuickView = this.isQuickview && this.getActiveOverlay === 'quickView'

      this.setBackInStockProductData({
        productId: this.product.id,
        variantId: this.variant.id,
        variantTitle: this.variant.title,
      })

      /**
       * Queue quick view again if viewing in quick view.
       */
      if (!queueQuickView) {
        return
      }

      this.queueOverlay({
        ignoreDismissed: true,
        namespace: 'quickView',
      })
    },

    /**
     * Handle add to cart submission.
     */
    async handleAddToCart() {
      if (this.addToCartDisabled) {
        return
      }

      clearTimeout(this.timeout)
      this.error = ''
      this.state = 'adding'

      if (cnvs.storybook) {
        this.timeout = setTimeout(() => {
          this.state = 'added'
        }, this.$timing('slow'))

        this.timeout = setTimeout(() => {
          this.state = 'ready'
        }, this.$timing('message'))

        return
      }

      /* _hide prefix to hide the property in checkout page */
      // eslint-disable-next-line no-underscore-dangle
      this.lineItemProperties._hide_product_title = this.product.product_title

      const items = [
        {
          id: this.variant.id,
          properties: this.lineItemProperties,
          quantity: this.quantity,
        },
      ]

      try {
        await this.addItem(items)
        this.state = 'added'

        if (this.getComponentExists('cart-drawer') && cnvs.page.type !== 'cart') {
          this.openOverlay({
            component: 'cart-drawer',
            ignoreDismissed: true,
            namespace: 'cartDrawer',
          })
        }

        await this.resetState()

      } catch (error) {
        this.state = 'error'

        if (error?.description) {
          this.error = error.description
          return
        }

        reportError('Failed to add product to cart', error)
      }
    },

    /**
     * Reset product form state after adding to cart after message delay.
     */
    async resetState() {
      if (!this.isQuickview) {
        await new Promise((resolve) => {
          setTimeout(resolve, this.$timing('message'))
        })
      }

      this.state = 'ready'

      /**
       * Revert to no variant selected.
       * - Unless product has single variant.
       */
      if (this.product.variants.length < 2) {
        return
      }

      this.handleVariantUpdate(false)
    },

    /**
     * Scroll to the complete-the-look section
     */
    scrollToCompleteTheLook() {
      const completeTheLookSection = document.querySelector('[data-component-handle="complete-the-look"]')
      if (completeTheLookSection) {
        scrollToElement(completeTheLookSection, {
          behavior: 'smooth',
        })
      }
    },

    /**
     * Initialize payment buttons after the Shopify script has loaded.
     */
    initShopifyPaymentButton() {
      if (window.Shopify && window.Shopify.PaymentButton) {
        window.Shopify.PaymentButton.init()

        return
      }

      setTimeout(this.initShopifyPaymentButton, this.$timing('slow'))
    },

    /**
     * Handle intersection observer for sticky add to cart button.
     */
    handleIntersectionObserver() {
      if (
        !window.IntersectionObserver ||
        !this.$refs.addToCartObserver ||
        !this.$refs.addToCartButton
      ) {
        return
      }

      this.intersectionObserver = new IntersectionObserver(
        ([entry]) => {
          if (this.isMobile) {
            const isAbove = entry.boundingClientRect.top < 0
            this.isFixedAddToCart = !entry.isIntersecting && isAbove
          } else {
            this.isFixedAddToCart = false
          }
        },
        {
          root: null,
          threshold: 0.01,
        },
      )
      this.intersectionObserver.observe(this.$refs.addToCartObserver)
    },

    /**
     * Scrolls smoothly to the product variants section.
     * This is typically used when the user needs to select a size/variant,
     * and the sticky size selector button is clicked.
     */
    scrollToVariants() {
      if (this.$refs.variantSection) {
        this.$refs.variantSection.$el.scrollIntoView({
          behavior: 'smooth',
          block: 'center',
        })
      }
    },
  },
}
</script>

<style lang="scss">
@import './product-form';
</style>
